import { CHANNEL_ID } from '@@types/common';
import Member from '@@types/Member';

import { ChannelType, Page } from '~/constants';
import { canUploadChannel, isDirectChannel, isPrivateChannel, isSharedChannel } from '~/services/channelService';
import { channelTenantsStore } from '~/stores/ChannelTenantsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { meteringStore } from '~/stores/MeteringStore';
import { officeHoursStore } from '~/stores/OfficeHoursStore';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { interTenantWebsocketStore, websocketStore } from '~/stores/WebsocketStore';
import { isNonEmpty, isNonNil, propNonEq } from '~/utils/fnUtil/fnUtil';
import { liftPredicate } from '~/utils/monadUtil';
import { noop } from 'lodash';
import { action, comparer, computed, observable, reaction } from 'mobx';
import { allPass, anyPass, filter, find, identity, includes, pipe, propEq, propSatisfies, take } from 'ramda';
import { Maybe } from 'ramda-fantasy';

import Actions from './Actions';
import { animationEffectStore } from './AnimationEffectStore';
import { channelLogsStore } from './ChannelLogsStore';
import { channelMailSettingStore } from './ChannelMailSettingStore';
import { channelMembersLangStore } from './ChannelMembersLangStore';
import { channelNoticesStore } from './ChannelNoticesStore';
import { channelReadSeqStore } from './ChannelReadSeqStore';
import { channelsStore } from './ChannelsStore';
import { coachMarkStore } from './CoachMarkStore';
import { doorayAppStore } from './DoorayAppStore';
import { editorHintStore } from './EditorHintStore';
import { emoticonStickerStore } from './EmoticonStickerStore';
import { favoritesStore } from './FavoritesStore';
import { forwardLogStore } from './ForwardLogStore';
import { globalSettingStore } from './GlobalSettingStore';
import { imageEditorStore } from './ImageEditorStore';
import { incomingHooksStore } from './IncomingHooksStore';
import { languagesStore } from './LanguagesStore';
import { membersStore } from './MembersStore';
import { notificationStore } from './NotificationStore';
import { organizationChartStore } from './OrganizationChartStore';
import { orgsStore } from './OrgsStore';
import { api } from './providers/Api2';
import router from './providers/router';
import { replyLogStore } from './ReplyLogStore';
import { searchStore } from './SearchStore';
import { sideAppsStore } from './SideAppsStore';
import { slashCommandAppsStore } from './SlashCommandAppsStore';
import { uiState } from './UiState';
import { userStore } from './UserStore';

class Stores {
  constructor() {
    reaction(() => this.locale, this.userStore.setIntlLocale);
  }

  uiState = uiState;
  globalSettingStore = globalSettingStore;
  slashCommandAppsStore = slashCommandAppsStore;
  channelsStore = channelsStore;
  searchStore = searchStore;
  channelTenantsStore = channelTenantsStore;
  channelReadSeqStore = channelReadSeqStore;
  channelNoticesStore = channelNoticesStore;
  channelMailSettingStore = channelMailSettingStore;
  favoritesStore = favoritesStore;
  notificationStore = notificationStore;
  membersStore = membersStore;
  interTenantMembersStore = interTenantMembersStore;
  orgsStore = orgsStore;
  userStore = userStore;
  organizationChartStore = organizationChartStore;
  channelLogsStore = channelLogsStore;
  doorayAppStore = doorayAppStore;
  incomingHooksStore = incomingHooksStore;
  sideAppsStore = sideAppsStore;
  languagesStore = languagesStore;
  channelMembersLangStore = channelMembersLangStore;
  imageEditorStore = imageEditorStore;
  coachMarkStore = coachMarkStore;
  forwardLogStore = forwardLogStore;
  replyLogStore = replyLogStore;
  editorHintStore = editorHintStore;
  meteringStore = meteringStore;
  animationEffectStore = animationEffectStore;
  websocketStore = websocketStore;
  interTenantWebsocketStore = interTenantWebsocketStore;
  officeHoursStore = officeHoursStore;
  mainDetailUiState = mainDetailUiState;
  threadDetailUiState = threadDetailUiState;
  threadChannelLogsStore = threadChannelLogsStore;
  threadChannelStore = threadChannelStore;
  emoticonStickerStore = emoticonStickerStore;

  @observable
  helpMailDomain = '';

  init = (actions: Actions) => {
    this.getHelpMailDomain();

    switch (window.__FILE_NAME__) {
      case '400':
        this.uiState.setCurrentPage(Page.Error400);
        break;
      case '404':
        this.uiState.setCurrentPage(Page.NotFound);
        break;
      case '500':
        this.uiState.setCurrentPage(Page.Error500);
        break;
      case 'orgs':
        this.uiState.setCurrentPage(Page.Orgs);
        globalSettingStore.cacheOrFetchEnv();
        break;
      case 'unauthorized':
        this.uiState.setCurrentPage(Page.Unauthorized);
        break;
      case 'unauthorized_ip':
        this.uiState.setCurrentPage(Page.UnauthorizedIP);
        break;
      default:
        this.initRouter(actions);
        break;
    }
  };

  initRouter = (actions: Actions) => {
    router.initialize({
      '/': () => {
        this.uiState.setCurrentPage(Page.Messenger);
        actions.bootstrapActions.bootstrapMessenger();
      },
      '/image/:channelId/:imageId': () => this.uiState.setCurrentPage(Page.PopupImage),
      '/popup/:channelId': (params, query) => {
        actions.bootstrapActions.bootstrapPopupChannel();
        if (isNonEmpty(query.forwardLogId)) {
          actions.logActions
            .setForwardLogById(params.channelId, query.forwardLogChannelId, query.forwardLogId)
            .catch(noop);
          router.redirect(`/popup/${params.channelId}`);
        } else {
          this.uiState.setCurrentPage(Page.PopupChannel);
        }
      },
      '/integration': () => {
        actions.bootstrapActions.bootstrapIntegration();
        this.uiState.setCurrentPage(Page.Integration);
      },
      '/orgs': () => {
        this.uiState.setCurrentPage(Page.Orgs);
        globalSettingStore.cacheOrFetchEnv();
      },
      '/400': () => this.uiState.setCurrentPage(Page.Error400),
      '/500': () => this.uiState.setCurrentPage(Page.Error500),
      '/unauthorized': () => this.uiState.setCurrentPage(Page.Unauthorized),
      '/unauthorized_ip': () => this.uiState.setCurrentPage(Page.UnauthorizedIP),
      '/share-channel': () => this.uiState.setCurrentPage(Page.ShareChannel),
      '*': () => this.uiState.setCurrentPage(Page.NotFound),
    });
  };

  @action
  setHelpMailDomain = (data) => {
    this.helpMailDomain = data;
  };

  getHelpMailDomain = () => {
    api.common.getHelpMailDomain().then(
      (data) => {
        this.setHelpMailDomain(data);
      },
      () => {}
    );
  };

  @computed
  get canInviteMembers() {
    const { inviteMember, inviteGuest } = this.userStore.effectiveFeatures || {};

    return inviteMember || inviteGuest;
  }

  @computed
  get isDefaultOrgAdmin() {
    return this.user.organizationMemberRoleMap[this.userStore.orgId]?.role === 'admin';
  }

  @computed
  get isMoreThanAdmin() {
    return this.userStore.isAdmin || this.userStore.isOwner;
  }

  @computed
  get isMoreThanOrgAdmin() {
    return this.isMoreThanAdmin || this.isDefaultOrgAdmin;
  }

  @computed({ keepAlive: true })
  get user(): Member {
    return this.membersStore.getById(this.userStore.id);
  }

  @computed({ keepAlive: true })
  get locale(): string {
    return take(2, this.user?.locale ?? '');
  }

  @computed({ keepAlive: true })
  get contentsChannel() {
    return this.channelsStore.getById(mainDetailUiState.contentsChannelId);
  }

  @computed({ keepAlive: true })
  get isDirectContentsChannel() {
    return isDirectChannel(this.contentsChannel);
  }

  @computed({ keepAlive: true })
  get isPrivateContentsChannel() {
    return isPrivateChannel(this.contentsChannel);
  }

  @computed({ keepAlive: true })
  get isSharedContentsChannel() {
    return isSharedChannel(this.contentsChannel);
  }

  @computed({ keepAlive: true })
  get isScreenCaptureableChannel() {
    return canUploadChannel(this.contentsChannel);
  }

  @computed({ keepAlive: true, equals: comparer.structural })
  get doorayAppCommands() {
    if (!this.contentsChannel) {
      return [];
    }
    const isAllOrChannelType = anyPass([
      propSatisfies(includes(ChannelType.ALL), 'channelTypes'),
      propSatisfies(includes(this.contentsChannel.type), 'channelTypes'),
    ]);
    const isMainViewOnlyCommand = this.uiState.isPopupView
      ? allPass([propNonEq('name', '/goto'), propNonEq('name', '/search'), propNonEq('name', '/status')])
      : identity;
    const isNonGuestCommand = this.userStore.isGuest
      ? allPass([propNonEq('id', 'invite'), propNonEq('id', 'form')])
      : identity;
    const isNonLitePlanCommand = this.globalSettingStore.isLiteVersion
      ? allPass([propNonEq('id', 'task'), propNonEq('id', 'mail'), propNonEq('id', 'event')])
      : identity;
    const canUseFormCommand =
      !this.globalSettingStore.isBusinessPlan || !this.globalSettingStore.canUseForm
        ? propNonEq('id', 'form')
        : identity;

    return filter(
      allPass([isAllOrChannelType, isMainViewOnlyCommand, isNonGuestCommand, isNonLitePlanCommand, canUseFormCommand]),
      doorayAppStore.getAllCommands()
    );
  }

  @computed({ keepAlive: true, equals: comparer.structural })
  get commandMentionRegex() {
    const candidates = ['task', 'mail', 'event', 'goto'];
    const matchCommands = candidates
      .filter((name) => this.doorayAppCommands.find(propEq('name', `/${name}`)))
      .join('|');
    return new RegExp(`^/(?:${matchCommands}) (?:.*)?@([^@ \\t]*)$`);
  }

  @computed({ keepAlive: true, equals: comparer.structural })
  get doorayApp(): { id: string; name: string; commands: any[] } {
    return {
      id: doorayAppStore.id,
      name: doorayAppStore.name,
      commands: stores.doorayAppCommands,
    };
  }

  @computed
  get doorayNewsChannelId() {
    return this.userStore.id;
  }

  isDoorayNewsChannelId = (channelId: CHANNEL_ID) => {
    return this.userStore.id === channelId;
  };

  safeGetDoorayCommand = (name: string): Maybe<string> => {
    return pipe(
      find(propEq('name', name)),
      liftPredicate(isNonNil)
    )(this.doorayAppCommands) as unknown as Maybe<string>;
  };

  hasDoorayCommand = (name: string) => {
    return Maybe.isJust(this.safeGetDoorayCommand(name));
  };
}

export default Stores;

export const stores = new Stores();
